import { organizationApp, OrganizationAppInfo } from '@/utils/organization_app';

// Support sessions: a super admin, from an organization's page, opens the app
// its people use (Screenkode or Chabad Digital) in a desktop window here, in
// this tab, signed in as that organization's admin. The API issues a token for
// the session (POST /supportSession); the app is loaded in a frame at
// SUPPORT_SESSION_PATH and, when it asks, handed the token over
// window.postMessage — never in a URL. Ending the session, from here or inside
// the app, tells the API, which refuses the token from then on.
//
// The app's side of this lives in each app's src/utils/support_session.ts;
// the names and messages below must match it.

/** The page in each app that opens a session: the frame loads it. */
export const SUPPORT_SESSION_PATH = '/support-session';

/**
 * The frame's name. The app reads it (window.name) to know it is in a session's
 * frame on any page, even after it reloads itself.
 */
export const SUPPORT_SESSION_FRAME_NAME = 'screenkode-support-session';

/** Every message between the admin and the app carries this, so unrelated messages are ignored. */
export const SUPPORT_SESSION_CHANNEL = 'screenkode.support-session';

export const SUPPORT_SESSION_MESSAGE = {
  /** app → admin: loaded, and needs its session. Sent until the admin answers. */
  READY: 'ready',
  /** admin → app: the session's token and account — sign in. */
  START: 'start',
  /** app → admin: signed in (the page it opened), or carrying on after a reload. */
  STARTED: 'started',
  /** app → admin: moved to another page (its path). */
  NAVIGATED: 'navigated',
  /** admin → app: the session is over — sign out. */
  END: 'end',
  /** app → admin: the session is over (signed out inside the app, refused by the API…). */
  ENDED: 'ended',
  /** app → admin: it couldn't use what it was sent. */
  FAILED: 'failed',
} as const;

/** How long a session may run, as the API sets it (SUPPORT_SESSION_TTL_MINUTES there). */
export const SUPPORT_SESSION_HOURS = 4;

/** The longest the app gets to sign in before the desktop says it isn't answering. */
export const SUPPORT_SESSION_BOOT_TIMEOUT_MS = 30 * 1000;

/** Once the frame has loaded, how long the app gets to ask for its session. */
export const SUPPORT_SESSION_READY_GRACE_MS = 10 * 1000;

/** The session, as the admin keeps it while the desktop is open (persisted across a reload). */
export interface SupportSessionRecord {
  id: string;
  organizationId: string;
  organizationName: string;
  organizationLogo: string | null;
  /** screenkode or chabad_digital */
  app: string;
  /** The role acted in inside the organization. */
  role: string;
  startedAt: string;
  expiresAt: string;
  sessionToken: string;
  /** The account the app signs in as: the admin, as the organization's admin. */
  account: Record<string, unknown>;
  /** The page the app was last on, so a reload of the admin opens it again. */
  lastPath?: string | null;
}

/** Why the app didn't finish signing in. */
export type SupportSessionStall = 'unreachable' | 'outdated' | 'no_signin';

/** The app a session runs in — Screenkode when the record names none it knows. */
export function supportSessionApp(session: Pick<SupportSessionRecord, 'app'> | null | undefined): OrganizationAppInfo {
  return organizationApp(session?.app) || (organizationApp('screenkode') as OrganizationAppInfo);
}

/** A page of the app, as a path the session may open ('/' and anything odd come back null). */
function appPath(path: string | null | undefined): string | null {
  if (!path || typeof path !== 'string' || path === '/') return null;
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return null;
  return path;
}

/**
 * Where the frame loads the app: its session page, for this session (the id
 * also keeps a cached copy of the page from being served), opening `path` once
 * signed in.
 */
export function supportSessionFrameUrl(session: Pick<SupportSessionRecord, 'app' | 'id'>, path?: string | null): string {
  const url = new URL(SUPPORT_SESSION_PATH, `${supportSessionApp(session).url}/`);
  url.searchParams.set('session', session.id);
  const next = appPath(path);
  if (next) url.searchParams.set('next', next);
  return url.toString();
}

/** The app's origin: the only window the desktop talks to, and listens to. */
export function supportSessionOrigin(session: Pick<SupportSessionRecord, 'app'>): string {
  try {
    return new URL(supportSessionApp(session).url).origin;
  } catch (error) {
    return supportSessionApp(session).url;
  }
}

/**
 * Plain data for window.postMessage. What comes out of the store is reactive
 * (a Proxy), which the browser refuses to send ("could not be cloned").
 */
export function plainCopy<T>(value: T): T {
  return value == null ? value : JSON.parse(JSON.stringify(value));
}

/** "3 h 58 min", "12 min", "under a minute". */
export function durationLabel(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds));
  if (seconds < 60) return 'under a minute';
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${minutes} min`;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

/** What the desktop says when the app didn't finish signing in, by why. */
export function supportSessionStallCopy(
  stall: SupportSessionStall | null,
  session: Pick<SupportSessionRecord, 'app'>
): { title: string; text: string } {
  const app = supportSessionApp(session);
  switch (stall) {
    case 'unreachable':
      return {
        title: `${app.name} didn't load`,
        text: `${app.host} couldn't be reached from here. Check your connection, then try again.`,
      };
    case 'outdated':
      return {
        title: `${app.name} didn't answer`,
        text: `${app.host} opened but never asked for the session. The version live there may not support sessions yet: deploy the latest ${app.name} build, then try again.`,
      };
    default:
      return {
        title: `${app.name} didn't finish signing in`,
        text: `${app.name} was sent the session but never confirmed it. Try again, or end the session.`,
      };
  }
}

/** What the desktop says once a session is over, by why. */
export function supportSessionEndedCopy(
  reason: string | null | undefined,
  message: string | null | undefined,
  session: Pick<SupportSessionRecord, 'app' | 'organizationName'>
): { title: string; text: string } {
  const app = supportSessionApp(session).name;
  switch (reason) {
    case 'signed_out':
      return { title: 'Signed out', text: `You signed out inside ${app}, which ended the session with ${session.organizationName}.` };
    case 'expired':
      return {
        title: 'Session expired',
        text: `Sessions run for up to ${SUPPORT_SESSION_HOURS} hours. Launch a new one from the organization's page to carry on.`,
      };
    case 'ended':
      return { title: 'Session ended', text: `The session with ${session.organizationName} is over.` };
    default:
      return { title: 'Session ended', text: message || `The session with ${session.organizationName} is no longer valid.` };
  }
}
